import { Fragment, FC } from 'react'
import { Link } from 'react-router-dom'
import { CircleFlag } from 'react-circle-flags'
import {
  ExistingPortItem,
  LagPortInfo,
} from '@/pages/ServiceWizard/ServiceWizard.types'
import { ConnectedService } from '@/lib/definitions/api.types'
import { SelectWithSearch } from '@/components/SelectInputs'
import Tooltip from '@/components/Tooltip'
import ArrowTopRightOnSquare from '@icons/arrow-top-right-on-square'
import { cn } from '@/lib/misc/Utils'

function formatSpeed(speedInMbps: number): string {
  if (speedInMbps >= 1000) return `${speedInMbps / 1000} Gbps`
  return `${speedInMbps} Mbps`
}

// ─── Tooltip content: service ─────────────────────────────────────────────────

const ServiceTooltipContent: FC<{ service: ConnectedService }> = ({
  service,
}) => (
  <div className="space-y-3">
    <div>
      <p className="text-sm font-semibold text-neutral-50 mb-1">
        {service.product_name}
      </p>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-medium text-neutral-90 leading-snug">
          {service.name}
        </p>
        {service.sid && (
          <span className="text-sm text-neutral-50 tabular-nums shrink-0">
            {service.sid}
          </span>
        )}
      </div>
    </div>

    {service.service_speed != null && (
      <p className="text-sm text-neutral-50">
        {formatSpeed(service.service_speed)} committed
      </p>
    )}

    {service.sid && (
      <div className="border-t border-hairline pt-2.5">
        <Link
          to={`/services/${service.sid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-neutral-50 hover:text-accent-60 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowTopRightOnSquare className="size-4 shrink-0" />
          Open service
        </Link>
      </div>
    )}
  </div>
)

// ─── Tooltip content: LAG ─────────────────────────────────────────────────────

const LagTooltipContent: FC<{
  lagPorts: LagPortInfo[]
  lagSco?: string
}> = ({ lagPorts, lagSco }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between gap-2">
      <p className="text-sm font-semibold text-neutral-50">
        LAG · {lagPorts.length} port{lagPorts.length !== 1 ? 's' : ''}
      </p>
      {lagSco && (
        <span className="text-sm text-neutral-50 tabular-nums">{lagSco}</span>
      )}
    </div>

    <div className="space-y-2.5">
      {lagPorts.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            {p.portType && (
              <p className="font-medium text-neutral-90">{p.portType}</p>
            )}
            {p.name && (
              <p className="text-sm text-neutral-50 truncate max-w-[140px]">
                {p.name}
              </p>
            )}
          </div>
          {p.sco && (
            <Link
              to={`/services/${p.sco}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-neutral-50 tabular-nums hover:text-accent-60 shrink-0 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {p.sco}
              <ArrowTopRightOnSquare className="size-3.5 shrink-0" />
            </Link>
          )}
        </div>
      ))}
    </div>

    {lagSco && (
      <div className="border-t border-hairline pt-2.5">
        <Link
          to={`/services/${lagSco}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-neutral-50 hover:text-accent-60 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowTopRightOnSquare className="size-4 shrink-0" />
          Open LAG
        </Link>
      </div>
    )}
  </div>
)

// ─── Tooltip content: port ────────────────────────────────────────────────────

export const PortTooltipContent: FC<{ port: ExistingPortItem }> = ({
  port,
}) => (
  <div className="space-y-3">
    <div>
      <p className="text-sm font-semibold text-neutral-50 mb-1">Port</p>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-medium text-neutral-90">
          {port.portSpeed?.speed
            ? `${port.portSpeed.speed}\u2009${port.portSpeed.unit}`
            : '-'}{' '}
          · {port.location.city.name}
        </p>
        {port.sco && (
          <span className="text-sm text-neutral-50 tabular-nums shrink-0">
            {port.sco}
          </span>
        )}
      </div>
    </div>

    {port.name && <p className="text-sm text-neutral-50">{port.name}</p>}
    {port.location.description && (
      <p className="text-sm text-neutral-50">{port.location.description}</p>
    )}

    {port.sco && (
      <div className="border-t border-hairline pt-2.5">
        <Link
          to={`/services/${port.sco}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-neutral-50 hover:text-accent-60 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowTopRightOnSquare className="size-4 shrink-0" />
          Open port
        </Link>
      </div>
    )}
  </div>
)

// ─── Shared badges ────────────────────────────────────────────────────────────

const ServiceBadge: FC<{ service: ConnectedService }> = ({ service }) => (
  <Tooltip
    inline
    clickable
    maxWidth={240}
    label={<ServiceTooltipContent service={service} />}
  >
    <span className="inline-flex items-center rounded-full bg-brand-5/10 px-2 py-0.5 text-sm font-medium text-accent-100 tabular-nums whitespace-nowrap select-none cursor-default">
      {service.sid ?? service.name}
    </span>
  </Tooltip>
)

const LagBadge: FC<{
  lagCount: number
  lagPorts?: LagPortInfo[]
  lagSco?: string
}> = ({ lagCount, lagPorts, lagSco }) => (
  <Tooltip
    inline
    clickable
    maxWidth={280}
    label={
      lagPorts && lagPorts.length > 0 ? (
        <LagTooltipContent lagPorts={lagPorts} lagSco={lagSco} />
      ) : (
        <span className="text-sm text-neutral-50">
          LAG with {lagCount} member ports
        </span>
      )
    }
  >
    <span className="inline-flex items-center rounded-full bg-neutral-20 px-2 py-0.5 text-xs font-semibold tracking-wide text-neutral-50 uppercase whitespace-nowrap select-none cursor-default">
      LAG {lagCount}×
    </span>
  </Tooltip>
)

// ─── Shared port row — used in both the dropdown list and the selected state ──

const PortRow: FC<{
  port: ExistingPortItem
  variant?: 'option' | 'value'
}> = ({ port, variant = 'value' }) => {
  const hasServices = (port.services?.length ?? 0) > 0
  const speed = port.portSpeed?.speed
    ? `${port.portSpeed.speed}\u2009${port.portSpeed.unit}`
    : undefined
  const metaParts = [
    port.location.name,
    port.location.city.name,
    port.location.description,
  ].filter(Boolean)

  return (
    <div
      className={cn(
        'min-w-0',
        variant === 'option'
          ? cn(
              'w-full',
              'before:absolute before:-top-[3px] before:left-0 before:right-0 before:h-px before:bg-neutral-20',
              'group-first/option:before:hidden'
            )
          : 'w-full py-0.5'
      )}
    >
      <div className="flex items-start gap-3 w-full min-w-0">
        <div className="w-5 h-5 shrink-0 mt-0.5">
          <CircleFlag
            countryCode={port.location.city.country.short_name.toLowerCase()}
            height="20"
          />
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-start justify-between gap-3 leading-6">
            <Tooltip
              inline
              clickable
              maxWidth={240}
              label={<PortTooltipContent port={port} />}
              className="flex min-w-0 items-center gap-2"
            >
              <span className="truncate font-semibold text-neutral-90 cursor-default">
                {port.name ?? port.sco ?? '-'}
              </span>
              {speed && (
                <span className="shrink-0 rounded-md bg-brand-1/5 px-1.5 py-0.5 text-sm font-semibold text-brand-1 cursor-default">
                  {speed}
                </span>
              )}
              {port.isLag && (
                <LagBadge
                  lagCount={port.lagCount}
                  lagPorts={port.lagPorts}
                  lagSco={port.sco}
                />
              )}
            </Tooltip>

            {hasServices && (
              <div className="flex shrink-0 items-center gap-1.5">
                {port.services!.map((s, i) => (
                  <ServiceBadge key={i} service={s} />
                ))}
              </div>
            )}
          </div>

          <div className="flex min-w-0 items-center gap-1.5 text-sm text-neutral-50">
            {port.sco && (
              <span className="shrink-0 text-sm text-neutral-50 tabular-nums">
                {port.sco}
              </span>
            )}
            {metaParts.map((part, i) => (
              <Fragment key={i}>
                {(i > 0 || port.sco) && (
                  <span className="text-neutral-30 shrink-0 select-none">
                    ·
                  </span>
                )}
                <span
                  className={cn(
                    i === metaParts.length - 1 ? 'truncate' : 'shrink-0'
                  )}
                >
                  {part}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

interface ExistingPortsDropdownSelectorProps {
  existingPorts: ExistingPortItem[]
  selectedExistingPort?: number
  onPortSelect: (port: ExistingPortItem | undefined) => void
  title?: string
  isOpen?: boolean
}

const ExistingPortsDropdownSelector: FC<ExistingPortsDropdownSelectorProps> = ({
  existingPorts,
  selectedExistingPort,
  onPortSelect,
  title = 'Select an existing port',
  isOpen = false,
}) => {
  const selectedPortData = selectedExistingPort
    ? existingPorts.find((port) => port.id === selectedExistingPort)
    : null

  return (
    <div>
      {title && (
        <h2 className="text-heading-byline tracking-tightest text-neutral-90 mb-2">
          {title}
        </h2>
      )}
      <SelectWithSearch
        cy="select-existing-port"
        label="Port"
        isOpen={isOpen}
        value={selectedPortData ? <PortRow port={selectedPortData} /> : ''}
        options={existingPorts.map((port) => ({
          value: port.id.toString(),
          label: [
            port.portSpeed?.speed
              ? `${port.portSpeed.speed} ${port.portSpeed.unit}`
              : '-',
            port.location.city.name,
            port.sco,
            port.name,
            ...(port.services?.map((s) => s.sid ?? '') ?? []),
          ]
            .filter(Boolean)
            .join(' '),
          className: 'relative my-[5px]',
          formattedLabel: <PortRow port={port} variant="option" />,
        }))}
        setValue={(value) => {
          if (value && typeof value === 'string' && !isNaN(Number(value))) {
            const port = existingPorts.find((p) => p.id.toString() === value)
            if (port) onPortSelect(port)
          }
        }}
        onReset={() => {
          if (selectedPortData) onPortSelect(undefined)
        }}
        required
      />
    </div>
  )
}

export default ExistingPortsDropdownSelector
